import { useState } from "react";
import { Link } from "react-router-dom";
import { BarChart2 } from "lucide-react";

export const StandingsSection = ({ standings, conferences, divisions, leagueColor, leagueSlug, leagueName }) => {
  const [conferenceFilter, setConferenceFilter] = useState("all");
  const [divisionFilter, setDivisionFilter] = useState("all");

  const getDivision = (divId) => divisions.find(d => d.id === divId);
  
  const getStandingDivisionId = (standing) => {
    return standing.division_entry?.division_id || standing.division?.id || null;
  };

  const getDivisionsForConference = (confId) => {
    const divisionIds = new Set();
    standings.forEach(s => {
      if (s.team?.conference_id === confId) {
        const divId = getStandingDivisionId(s);
        if (divId) divisionIds.add(divId);
      }
    });
    return divisions.filter(d => divisionIds.has(d.id));
  };

  const getFilteredStandings = () => {
    return standings.filter(standing => {
      if (conferenceFilter !== "all") {
        if (standing.team?.conference_id !== conferenceFilter) return false;
      }
      if (divisionFilter !== "all") {
        const divId = getStandingDivisionId(standing);
        if (divId !== divisionFilter) return false;
      }
      return true;
    });
  };

  const sortStandings = (teamList) => {
    return [...teamList].sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      const aDiff = (a.points_for || 0) - (a.points_against || 0);
      const bDiff = (b.points_for || 0) - (b.points_against || 0);
      return bDiff - aDiff;
    });
  };

  const getTeamsForDivision = (confId, divId) => {
    return standings.filter(s => {
      if (s.team?.conference_id !== confId) return false;
      const standingDivId = getStandingDivisionId(s);
      return standingDivId === divId;
    });
  };

  const getTeamsWithoutDivision = (confId) => {
    return standings.filter(s => {
      if (s.team?.conference_id !== confId) return false;
      const divId = getStandingDivisionId(s);
      return !divId;
    });
  };

  const buildHierarchy = () => {
    const filteredStandings = getFilteredStandings();
    
    if (divisionFilter !== "all") {
      const division = getDivision(divisionFilter);
      const conf = conferences.find(c => c.id === division?.conference_id);
      return [{
        type: "division",
        name: division?.name || "Division",
        color: division?.color || conf?.color || leagueColor,
        logo_url: conf?.logo_url,
        teams: sortStandings(filteredStandings)
      }];
    }
    
    if (conferenceFilter !== "all") {
      const conf = conferences.find(c => c.id === conferenceFilter);
      const confDivisions = getDivisionsForConference(conferenceFilter);
      const sections = [];
      
      confDivisions.forEach(div => {
        const divTeams = getTeamsForDivision(conferenceFilter, div.id);
        if (divTeams.length > 0) {
          sections.push({
            type: "division",
            name: div.name,
            color: div.color || conf?.color || leagueColor,
            logo_url: conf?.logo_url,
            conference: conf,
            teams: sortStandings(divTeams)
          });
        }
      });
      
      const noDivTeams = getTeamsWithoutDivision(conferenceFilter);
      if (noDivTeams.length > 0) {
        sections.push({
          type: "division",
          name: "Other Teams",
          color: conf?.color || leagueColor,
          logo_url: conf?.logo_url,
          teams: sortStandings(noDivTeams)
        });
      }
      
      if (sections.length === 0) {
        sections.push({
          type: "conference",
          name: conf?.name || "Conference",
          color: conf?.color || leagueColor,
          logo_url: conf?.logo_url,
          teams: sortStandings(filteredStandings)
        });
      }
      
      return sections;
    }
    
    const hierarchy = [];
    
    conferences.forEach(conf => {
      const confDivisions = getDivisionsForConference(conf.id);
      
      if (confDivisions.length > 0) {
        hierarchy.push({
          type: "conference-header",
          name: conf.name,
          color: conf.color || leagueColor,
          logo_url: conf.logo_url,
          abbreviation: conf.abbreviation
        });
        
        confDivisions.forEach(div => {
          const divTeams = getTeamsForDivision(conf.id, div.id);
          if (divTeams.length > 0) {
            hierarchy.push({
              type: "division",
              name: div.name,
              abbreviation: div.abbreviation,
              color: div.color || conf.color || leagueColor,
              parentConference: conf.name,
              teams: sortStandings(divTeams)
            });
          }
        });
        
        const noDivTeams = getTeamsWithoutDivision(conf.id);
        if (noDivTeams.length > 0) {
          hierarchy.push({
            type: "division",
            name: "Other",
            color: conf.color || leagueColor,
            parentConference: conf.name,
            teams: sortStandings(noDivTeams)
          });
        }
      } else {
        const confTeams = filteredStandings.filter(s => s.team?.conference_id === conf.id);
        if (confTeams.length > 0) {
          hierarchy.push({
            type: "conference",
            name: conf.name,
            color: conf.color || leagueColor,
            logo_url: conf.logo_url,
            teams: sortStandings(confTeams)
          });
        }
      }
    });
    
    const unassigned = filteredStandings.filter(s => !s.team?.conference_id);
    if (unassigned.length > 0) {
      hierarchy.push({
        type: "conference",
        name: "Unassigned",
        color: leagueColor,
        teams: sortStandings(unassigned)
      });
    }
    
    return hierarchy;
  };

  const hierarchy = buildHierarchy();
  const hasFilters = conferences.length > 0 || divisions.length > 0;

  const renderTable = (teams) => (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-zinc-500 border-b border-zinc-800">
          <th className="px-4 py-3 font-medium w-10">#</th>
          <th className="px-4 py-3 font-medium">Team</th>
          <th className="px-4 py-3 font-medium text-center">W</th>
          <th className="px-4 py-3 font-medium text-center">L</th>
          <th className="px-4 py-3 font-medium text-center hidden sm:table-cell">PCT</th>
          <th className="px-4 py-3 font-medium text-center hidden md:table-cell">STRK</th>
        </tr>
      </thead>
      <tbody>
        {teams.length === 0 ? (
          <tr>
            <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
              No teams in standings
            </td>
          </tr>
        ) : (
          teams.map((standing, idx) => {
            const winPct = standing.wins + standing.losses > 0
              ? (standing.wins / (standing.wins + standing.losses)).toFixed(3)
              : ".000";
            const teamSlug = standing.team?.slug || standing.team?.name?.toLowerCase().replace(/\s+/g, '-');
            const uniqueKey = standing.division_entry_id || standing.team_id || idx;
            const displayName = standing.display_name || standing.division_entry?.display_name || standing.team?.name || "Unknown Team";
            
            return (
              <tr key={uniqueKey} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                <td className="px-4 py-3 text-zinc-500">{idx + 1}</td>
                <td className="px-4 py-3">
                  <Link 
                    to={`/team/${teamSlug}`}
                    state={{ 
                      from: "league", 
                      tab: "standings", 
                      leagueSlug: leagueSlug,
                      leagueName: leagueName 
                    }}
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    {standing.team?.logo_url ? (
                      <img 
                        src={standing.team.logo_url} 
                        alt="" 
                        className="w-8 h-8 object-contain"
                      />
                    ) : (
                      <div 
                        className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: standing.team?.color || "#333" }}
                      >
                        {standing.team?.abbreviation || standing.team?.name?.substring(0, 2)}
                      </div>
                    )}
                    <div>
                      <span className="text-white font-medium hover:text-orange-400 transition-colors">
                        {displayName}
                      </span>
                      {standing.team?.city && (
                        <span className="text-zinc-500 text-xs ml-2 hidden sm:inline">
                          {standing.team.city}
                        </span>
                      )}
                    </div>
                  </Link>
                </td>
                <td className="px-4 py-3 text-center text-white font-medium">{standing.wins}</td>
                <td className="px-4 py-3 text-center text-white">{standing.losses}</td>
                <td className="px-4 py-3 text-center text-zinc-400 hidden sm:table-cell">{winPct}</td>
                <td className="px-4 py-3 text-center hidden md:table-cell">
                  {standing.streak ? (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      standing.streak.startsWith('W') 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {standing.streak}
                    </span>
                  ) : (
                    <span className="text-zinc-600">-</span>
                  )}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-6">
      {hasFilters && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="font-heading text-xl uppercase text-white tracking-wide">
            Standings
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            {conferences.length > 0 && (
              <select
                value={conferenceFilter}
                onChange={(e) => {
                  setConferenceFilter(e.target.value);
                  setDivisionFilter("all");
                }}
                className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                data-testid="standings-conference-filter"
              >
                <option value="all">All Conferences</option>
                {conferences.map(conf => (
                  <option key={conf.id} value={conf.id}>{conf.name}</option>
                ))}
              </select>
            )}
            {divisions.length > 0 && (
              <select
                value={divisionFilter}
                onChange={(e) => setDivisionFilter(e.target.value)}
                className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                data-testid="standings-division-filter"
              >
                <option value="all">All Divisions</option>
                {divisions.map(div => (
                  <option key={div.id} value={div.id}>{div.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {hierarchy.map((section, idx) => {
          if (section.type === "conference-header") {
            return (
              <div key={`conf-${idx}`} className="pt-4 first:pt-0">
                <div 
                  className="px-4 py-3 rounded-t-lg flex items-center gap-3"
                  style={{ backgroundColor: `${section.color}25` }}
                >
                  {section.logo_url && (
                    <img 
                      src={section.logo_url} 
                      alt={section.name} 
                      className="w-10 h-10 object-contain"
                    />
                  )}
                  <h3 className="font-heading text-lg uppercase text-white tracking-wide">
                    {section.name}
                  </h3>
                </div>
              </div>
            );
          }
          
          if (section.type === "division") {
            return (
              <div 
                key={`div-${idx}`}
                className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden ml-0 sm:ml-4"
              >
                <div 
                  className="px-4 py-2 border-b border-zinc-800 flex items-center gap-2"
                  style={{ backgroundColor: `${section.color}15` }}
                >
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: section.color }}
                  />
                  <h4 className="text-sm font-medium text-white uppercase tracking-wide">
                    {section.name}
                  </h4>
                  <span className="text-xs text-zinc-500">({section.teams.length})</span>
                </div>
                <div className="overflow-x-auto">
                  {renderTable(section.teams)}
                </div>
              </div>
            );
          }
          
          if (section.type === "conference") {
            return (
              <div 
                key={`conf-teams-${idx}`}
                className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden"
              >
                <div 
                  className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3"
                  style={{ backgroundColor: `${section.color}20` }}
                >
                  {section.logo_url && (
                    <img 
                      src={section.logo_url} 
                      alt={section.name} 
                      className="w-10 h-10 object-contain"
                    />
                  )}
                  <h3 className="font-heading text-lg uppercase text-white tracking-wide">
                    {section.name}
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  {renderTable(section.teams)}
                </div>
              </div>
            );
          }
          
          return null;
        })}
        
        {hierarchy.length === 0 && (
          <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-12 text-center">
            <BarChart2 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No standings available</p>
          </div>
        )}
      </div>
    </div>
  );
};
